import { useEffect } from 'react';

import { usePlayer } from '../../player/hooks/usePlayer';

export interface SeekParam {
  currentTime?: number;
  duration?: number;
  position?: number;
  metadata?: {
    currentTime: number;
  };
  offset: number;
  seekRange?: {
    start: number;
    end: number;
  };
  type: 'seek';
}

export type AdMetaParam = {
  adId: string;
  duration: number;
} & (
  | {
      client: 'googima';
      ima: {
        ad: {
          h: {
            vastMediaWidth: number;
            vastMediaHeight: number;
          };
        };
      };
    }
  | {
      client: 'vast';
      sequence: number;
      response: XMLDocument;
    }
);

export interface AdStartedParam {
  meta: AdMetaParam;
  aspectRatio?: number;
}

interface EventParams {
  seek: SeekParam;
  adStarted: AdStartedParam;
  error: jwplayer.ErrorParam;
  adMeta: AdMetaParam;
}

type NoParamEvent = 'play' | 'pause' | 'ready';

function usePlayerEvent(eventName: NoParamEvent, listener: () => void): void;
function usePlayerEvent<T extends keyof EventParams>(
  eventName: T,
  listener: (param: EventParams[T]) => void
): void;
function usePlayerEvent(
  eventName: 'cast',
  listener: (event: jwplayer.CastParam) => void
): void;
function usePlayerEvent(
  eventName: 'time',
  listener: (time: number, duration: number) => void
): void;
function usePlayerEvent(eventName: string, listener: () => void): void;
function usePlayerEvent(
  eventName: string,
  listener: (...args: any[]) => void
): void {
  const player = usePlayer();

  useEffect(() => {
    if (!player || !listener) {
      return;
    }

    player.on(eventName, listener);

    return () => {
      player.off(eventName, listener);
    };
  }, [player, eventName, listener]);
}

export { usePlayerEvent };
